import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import AuthTextField from './AuthTextField';
import { authCardStyle, authButtonStyle } from '../constants/authStyles';
import { usePopupMessage } from '../hooks/usePopupMessage';

const Register = () => {
  const { t } = useTranslation('selection');
  const { t: tc } = useTranslation('common');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    passwordConfirm: ''
  });
  const { setError, clearError } = usePopupMessage();
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    clearError();
    setSuccess('');
  };

  const validateForm = () => {
    if (!formData.username || !formData.email || !formData.password || !formData.passwordConfirm) {
      setError(t('errorAllFields'));
      return false;
    }

    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    if (!usernameRegex.test(formData.username)) {
      setError(t('errorUsernameFormat'));
      return false;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError(t('errorPasswordMismatch'));
      return false;
    }

    if (formData.password.length < 6) {
      setError(t('errorPasswordLength'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('errorInvalidEmail'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/register', formData);
      
      if (response.data.success) {
        setSuccess(t('registerSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(response.data.message || t('registerFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || tc('errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={authCardStyle}>
        <div className="auth-header">
          <h1>{t('registerTitle')}</h1>
          <p>{t('registerSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {success && <div className="success-message">{success}</div>}

          <div className="form-group">
            <label htmlFor="username">{t('username')}</label>
            <AuthTextField
              id="username"
              name="username"
              type="text"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder={t('usernamePlaceholder')}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <AuthTextField
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('emailPlaceholder')}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <AuthTextField
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={t('passwordRegisterPlaceholder')}
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="passwordConfirm">{t('passwordConfirm')}</label>
            <AuthTextField
              id="passwordConfirm"
              name="passwordConfirm"
              type="password"
              value={formData.passwordConfirm}
              onChange={handleChange}
              required
              placeholder={t('passwordConfirmPlaceholder')}
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            style={authButtonStyle}
            disabled={loading}
          >
            {loading ? t('registerSubmitting') : t('registerSubmit')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('hasAccount')}{' '}
            <Link to="/login" className="auth-link">
              {t('loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
